import { ImagePlus, Plus, Video } from "lucide-react";
import { FC, useCallback } from "react";

import {
  FileUploadProvider,
  useFileUpload,
} from "@/components/file-upload-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCMSStore } from "@/hooks/use-cms-store";
import { FileType, type UploadedFile } from "@/lib/uploads";

import { useCMSTabContext } from "../cms-tab-context";

const AddContentButtonInner = () => {
  const {
    isUploading,
    uploadProgress,
    currentFileCount,
    totalFileCount,
    openFilePicker,
  } = useFileUpload();

  const handleAddImage = useCallback(() => {
    openFilePicker(FileType.IMAGE);
  }, [openFilePicker]);

  const handleAddVideo = useCallback(() => {
    openFilePicker(FileType.VIDEO);
  }, [openFilePicker]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          className="w-full mb-0"
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              {`${uploadProgress}% - Files ${currentFileCount} of ${totalFileCount}`}
            </>
          ) : (
            <>
              <Plus className="size-4" /> Add Content
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" className="w-60">
        <DropdownMenuItem onClick={handleAddImage}>
          <ImagePlus className="size-4" /> Image File
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleAddVideo}>
          <Video className="size-4" /> Video File
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const AddContentButton: FC = () => {
  const { tabIndex } = useCMSTabContext();
  const onAdd = useCMSStore.use.onAddGalleryItems();

  const handleFileUploadFinish = useCallback(
    (uploadedFiles: UploadedFile[]) => {
      onAdd(tabIndex, uploadedFiles);
    },
    [onAdd, tabIndex],
  );

  return (
    <FileUploadProvider multiple onFinish={handleFileUploadFinish}>
      <AddContentButtonInner />
    </FileUploadProvider>
  );
};
